import React, { useState } from 'react';
import { Main } from '../theming/styles';
import styled, { createGlobalStyle } from 'styled-components';
import { Nav } from '../components';
import { SiteFooter } from '../components/foot/SiteFooter';
import { Spread } from '../components/switch/styles';
import { Link } from 'react-router-dom';
import OutlineWebJourney from '../components/OutlineWebJourney';
import { SafliteMockup, SafliteMockup2, SafliteMockupBefore, SafliteMockupAfter } from '../images';
import { ImageComparison } from '../components/ui/image-comparison-slider';

const GlobalStyle = createGlobalStyle`
  html { scroll-behavior: smooth; }
`;

/* ── Page shell — mirrors Dubi exactly ── */
const PageLayout = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: flex-start;
    padding: 0 20px;
`;

const NavColumn = styled.div`
    width: 18%;
    flex-shrink: 0;
    position: sticky;
    top: 90px;
    align-self: flex-start;
    padding-top: 52px;

    @media (max-width: 1099px) {
        display: none;
    }
`;

const ContentColumn = styled.div`
    flex: 1;
    min-width: 0;

    h4 { font-weight: 500; }
`;

/* ── Header area (back + meta + title + hero) ── */
const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-body);
    font-size: 14px;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.5;
    margin-bottom: 32px;
    transition: opacity 0.15s ease;
    &:hover { opacity: 1; }
`;

const MetaRow = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.45;
    margin: 0 0 20px;
    letter-spacing: 0.02em;
`;

/* ── NDA banner ── */
const NdaBanner = styled.div`
    border: 1px solid ${({ theme }) => theme.outline};
    border-radius: 16px;
    padding: 28px 32px;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 768px) {
        padding: 24px 20px;
    }
`;

const NdaTitle = styled.p`
    font-family: var(--font-body);
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.textMain};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const NdaBody = styled.p`
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.6;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.6;
    margin: 0;
`;

const NdaActions = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 8px;
    flex-wrap: wrap;
`;

const BookCallBtn = styled.a`
    display: inline-flex;
    align-items: center;
    background: ${({ theme }) => theme.flip};
    color: ${({ theme }) => theme.main};
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: var(--radius-pill);
    text-decoration: none;
    transition: opacity 0.15s ease;
    &:hover { opacity: 0.82; }
`;

const EmailLink = styled.a`
    font-family: var(--font-body);
    font-size: 13px;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.5;
    text-decoration: underline;
    text-underline-offset: 3px;
    &:hover { opacity: 1; }
`;

/* ── Dubi-style principle grid for highlights ── */
const PrincipleGrid = styled.div`
    display: flex;
    gap: 8px;
    background: var(--color-surface);
    border-radius: 18px;
    padding: 8px;
    margin: 20px 0;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const PrincipleCard = styled.div`
    flex: 1;
    background: var(--color-bg);
    border-radius: 12px;
    padding: 20px;
`;

/* ── Shuffle deck (copied from Dubi) ── */
const CASE_STUDIES = [
    {
        tags: ['Design System', 'Safaricom PLC'],
        title: 'From Fragmentation to a System 100+ People Actually Use',
        desc: 'How I rebuilt Safaricom\'s design system from the ground up — unifying tokens, components, and documentation across products to cut handoff friction and ship faster.',
        href: '/dira',
        bg: '#e4ecfb',
        preview: require('../images/dira/dira-cover-image.png'),
    },
    {
        tags: ['Product Design', 'Safaricom PLC'],
        title: 'Dubi — Survey & Feedback Platform',
        desc: 'Turned 50M+ Safaricom users into a listening machine — 92M conversations across Kenya & Ethiopia in 3 months.',
        href: '/dubi',
        bg: '#dff0ea',
        preview: require('../images/dubi/dub-preview.png'),
    },
    {
        tags: ['UX Design', 'Safaricom PLC'],
        title: 'My Safaricom Partner Portal Redesign',
        desc: 'Unified a fragmented dealer ecosystem into a single SaaS portal — 90M KES in orders, month one.',
        href: '/partner-portal',
        bg: '#f0e8fb',
        preview: require('../images/schedule/thumb.png'),
    },
];

const BrowserCard = ({ cs }) => (
    <div className="browser-card" style={{
        background: '#fff',
        borderRadius: 20,
        padding: '32px',
        display: 'flex',
        gap: 32,
        alignItems: 'stretch',
        minHeight: 340,
        boxSizing: 'border-box',
        boxShadow: '0 8px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
    }}>
        {/* Browser mockup */}
        <div className="browser-mockup" style={{
            flex: '0 0 45%',
            background: '#fff',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 24px rgba(0,0,0,0.09)',
        }}>
            <div style={{
                background: '#f0f0f0',
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                borderBottom: '1px solid #e5e5e5',
                flexShrink: 0,
            }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                <div style={{ flex: 1, marginLeft: 8, background: '#e2e2e2', borderRadius: 5, height: 20, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
                    <span style={{ fontSize: 9, opacity: 0.5, fontFamily: 'var(--font-body)' }}>kimanigodfrey.bio{cs.href}</span>
                </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {cs.preview && (
                    <img src={cs.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                )}
            </div>
        </div>
        {/* Text */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ background: 'rgba(3,14,73,0.1)', color: '#030e49', borderRadius: 100, padding: '4px 14px', fontSize: 11.5, fontWeight: 600, fontFamily: 'var(--font-body)' }}>{cs.tags[0]}</span>
                <span style={{ fontSize: 12, opacity: 0.4, fontFamily: 'var(--font-body)' }}>{cs.tags[1]}</span>
            </div>
            <h3 style={{ margin: 0, lineHeight: 1.2, fontSize: 'clamp(16px,2vw,22px)' }}>{cs.title}</h3>
            <p className="text-style" style={{ margin: 0, fontSize: 13 }}>{cs.desc}</p>
            <a href={cs.href} className="btn-primary">
                Read case study
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </a>
        </div>
    </div>
);

const MoreCaseStudies = () => {
    const [order, setOrder] = useState([0, 1, 2]);
    const shuffle = () => setOrder(prev => [...prev.slice(1), prev[0]]);

    const OFFSET_Y = 46;
    const SCALE_STEP = 0.04;
    const numCards = order.length;
    const peekHeight = (numCards - 1) * OFFSET_Y;

    return (
        <div className="case-deck-wrap" style={{ background: 'var(--color-surface)', borderRadius: 24, padding: '50px' }}>
            <div style={{ position: 'relative', paddingTop: peekHeight }}>
                <div style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                    <BrowserCard cs={CASE_STUDIES[0]} />
                </div>
                {order.map((csIdx, stackPos) => {
                    const ty = -stackPos * OFFSET_Y;
                    const scale = 1 - stackPos * SCALE_STEP;
                    const zIdx = numCards - stackPos;
                    return (
                        <div
                            key={csIdx}
                            style={{
                                position: 'absolute',
                                top: peekHeight,
                                left: 0,
                                right: 0,
                                zIndex: zIdx,
                                transform: `translateY(${ty}px) scale(${scale})`,
                                transformOrigin: 'bottom center',
                                transition: 'transform 400ms ease-out',
                            }}
                        >
                            <BrowserCard cs={CASE_STUDIES[csIdx]} />
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                <button onClick={shuffle} className="btn-text">
                    Shuffle
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
                        <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

const WebJourney = ({ toggleMode, mode, spread }) => {
    const spreadClass =
        spread === 'first' ? 'growBlack' :
        spread === 'second' ? 'growWhite' : '';

    return (
        <Main>
            <GlobalStyle />
            <Spread className={spreadClass} />
            <Nav mode={mode} toggleMode={toggleMode} />

            <PageLayout>
                <NavColumn>
                    <OutlineWebJourney />
                </NavColumn>

                <ContentColumn>
                    {/* ── Header ── */}
                    <div id="summary" className="card-pad" style={{ maxWidth: 'none', margin: 0 }}>
                        <BackLink to="/">← Back</BackLink>

                        <MetaRow>Confidential &nbsp;·&nbsp; 2025</MetaRow>

                        <h2 style={{ marginBottom: 28 }}>
                            Unifying the One App Lite web experience for 1M+ customers—eliminating friction and aligning key flows.
                        </h2>

                        <p className="text-style" style={{ marginBottom: 16 }}>
                            As Lead Product Designer, I drove the end-to-end redesign of the My Safaricom web platform — auditing 40+ flows, unifying navigation, and aligning every screen to one consistent interaction language.
                        </p>
                        <p className="text-style" style={{ marginBottom: 32 }}>
                            The platform had grown organically across multiple teams over years, leaving customers with inconsistent patterns and critical drop-off points. This project brought it all together into one coherent, frictionless experience for over a million web customers.
                        </p>

                        {/* Hero — before/after comparison slider */}
                        <div style={{ marginBottom: 32 }}>
                            <ImageComparison
                                beforeImage={SafliteMockupAfter}
                                afterImage={SafliteMockupBefore}
                                altBefore="Before — old web experience"
                                altAfter="After — Mysaflite App redesign"
                            />
                        </div>

                        {/* NDA */}
                        <NdaBanner>
                            <NdaTitle><span>🔒</span> Project under NDA</NdaTitle>
                            <NdaBody>
                                This project is confidential. If you're a hiring manager or recruiter, reach out and I'll walk you through the full case study.
                            </NdaBody>
                            <NdaActions>
                                <BookCallBtn href="mailto:kimanigodfrey4@gmail.com">Book a call</BookCallBtn>
                                <EmailLink href="mailto:kimanigodfrey4@gmail.com">kimanigodfrey4@gmail.com</EmailLink>
                            </NdaActions>
                        </NdaBanner>
                    </div>

                    {/* ── Highlights ── */}
                    <div id="highlights" className="card-pad" style={{ marginBottom: 0 }}>
                        <h3 style={{ margin: '0 0 24px' }}>Simplifying the experience for millions</h3>
                        <p className="text-style" style={{ marginBottom: 24 }}>
                            The One App Lite web platform had grown organically across multiple teams — different flows, inconsistent navigation patterns, and a fragmented visual language. Customers were dropping off at critical points and struggling to complete core tasks.
                        </p>

                        <div id="key-outcomes">
                            <PrincipleGrid>
                                {[
                                    {
                                        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
                                        label: 'Audit',
                                        body: 'Mapped and audited 40+ user flows across the existing web platform to identify drop-off points and inconsistencies.',
                                    },
                                    {
                                        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
                                        label: 'Navigation',
                                        body: 'Unified navigation architecture across all top-level services — reducing task completion steps by an average of 3 clicks.',
                                    },
                                    {
                                        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>,
                                        label: 'Consistency',
                                        body: 'Established a consistent visual and interaction language aligned with the Dira design system across the full web journey.',
                                    },
                                ].map(({ icon, label, body }) => (
                                    <PrincipleCard key={label}>
                                        <div style={{ color: 'var(--color-text)', marginBottom: 16, opacity: 0.6 }}>{icon}</div>
                                        <h5 style={{ marginBottom: 10, fontWeight: 700 }}>{label}</h5>
                                        <p className="text-style" style={{ margin: 0 }}>{body}</p>
                                    </PrincipleCard>
                                ))}
                            </PrincipleGrid>

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', background: 'var(--color-surface)', borderRadius: 18, padding: 8, margin: '20px 0 0' }}>
                                {[
                                    {
                                        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
                                        label: 'Cross-team Collaboration',
                                        body: 'Worked with engineering, product, brand, and research teams to align on a single source-of-truth for the web experience.',
                                    },
                                    {
                                        icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
                                        label: 'Scale',
                                        body: 'Shipped a redesigned experience serving over 1 million web customers across Safaricom\'s One App Lite platform.',
                                    },
                                ].map(({ icon, label, body }) => (
                                    <div key={label} style={{ flex: 1, minWidth: 220, background: 'var(--color-bg)', borderRadius: 12, padding: 20 }}>
                                        <div style={{ color: 'var(--color-text)', marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                                        <h5 style={{ marginBottom: 10 }}>{label}</h5>
                                        <p className="text-style">{body}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Quick Snapshot ── */}
                    <div className="card-pad" style={{ marginBottom: 0 }}>
                        <h3 style={{ margin: '0 0 24px' }}>Quick snapshot</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                            {[
                                { stat: '40+', label: 'User flows audited' },
                                { stat: '1M+', label: 'Web customers impacted' },
                                { stat: '−3', label: 'Avg. clicks to complete a task' },
                                { stat: '1', label: 'Unified design language' },
                            ].map(({ stat, label }) => (
                                <div key={label} style={{ background: 'var(--color-surface)', borderRadius: 16, padding: '28px 24px' }}>
                                    <p style={{ margin: '0 0 8px', fontSize: 40, fontWeight: 800, fontFamily: 'var(--font-display)', letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--color-text)' }}>{stat}</p>
                                    <p className="text-style" style={{ margin: 0, opacity: 0.55, fontSize: 13 }}>{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Retrospective ── */}
                    <div id="retrospective" className="card-pad" style={{ marginBottom: 0 }}>
                        <h3 style={{ margin: '0 0 24px' }}>Made the web work the way customers expected</h3>
                        <p className="text-style" style={{ marginBottom: 24 }}>
                            The redesign brought clarity to a platform that had grown complex over time. By starting with a thorough audit and aligning every flow to real customer journeys, we removed the friction that was quietly costing Safaricom conversions and customer trust.
                        </p>
                        <p className="text-style" style={{ marginBottom: 0 }}>
                            The biggest win was not just the visual polish — it was the consistency that finally made the platform feel like one product, not ten.
                        </p>
                        <blockquote style={{ borderLeft: '3px solid var(--color-primary)', padding: '10px 20px', margin: '32px 0 0', fontStyle: 'italic', color: 'var(--color-primary)', fontFamily: 'var(--font-display)', fontSize: '18px', letterSpacing: '-0.3px' }}>
                            "Godfrey's ability to hold the big picture and the fine detail at the same time is rare. The web redesign wasn't just a facelift — it fundamentally changed how our customers interact with us online."
                        </blockquote>
                        <p style={{ margin: '12px 0 0 23px', fontSize: 13, fontWeight: 600, opacity: 0.45, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-body)' }}>
                            Senior Product Manager, Safaricom PLC
                        </p>
                    </div>

                    {/* ── More case studies (shuffle deck) ── */}
                    <div id="other-projects" className="card-pad more-case-studies-wrap" style={{ marginBottom: 150 }}>
                        <h3 style={{ margin: '0 0 32px' }}>More case studies</h3>
                        <MoreCaseStudies />
                    </div>

                </ContentColumn>
            </PageLayout>

            <SiteFooter />
        </Main>
    );
};

export { WebJourney };
